import React, { useContext, useEffect, useState } from 'react'
import { GridItem } from '@zuto/core/src/grid'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import Button from '@zuto/atoms/src/Mui/Button/Button'
import { Loading } from '@zuto/molecules'
import QuestionComponentProps from '../../../Questions/question-component-props'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import AppFormQuestion from '../../AppFormQuestion'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import withFade from '../../HOC/withFade'
import withQuestion from '../../HOC/withQuestion'
import { VehicleDetailsDataObject } from '../../../services/vehicle-service'
import { getVehicleIdentity } from '../../../utils/api/getVehicleIdentity'
import { handlePrevButtonClick, scrollToTheTop } from '../utils'
import { PrefillReviewPageApplicationFormContext } from '../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import VehicleInfoRefinance from './VehicleInfoRefinance'
import { Box, Checkbox, FormControlLabel } from '@mui/material'

const VRM_PATTERN = /^[A-Za-z0-9]{4,8}$/

const VehicleRegistrationLookup = ({
  existingAnswers,
  disablePrevious,
  updateFollowingPrefillStep
}: QuestionComponentProps) => {
  const { hasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const { dispatch } = useContext(ApplicationFormContext)
  const [registration, setRegistration] = useState(existingAnswers?.registration ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [vehicle, setVehicle] = useState<VehicleDetailsDataObject | undefined>(existingAnswers)
  const [dontKnow, setDontKnow] = useState(false)
  const [isValid, setIsValid] = useState(VRM_PATTERN.test(registration.trim()))

  useEffect(scrollToTheTop, [])

  const handleFindVehicle = async () => {
    if (!isValid || loading) return
    setLoading(true)
    setError('')
    try {
      const foundVehicle = await getVehicleIdentity(registration.trim().toUpperCase())
      setVehicle(foundVehicle)
    } catch (e) {
      setError("We couldn't find a vehicle with that registration. Please check it and try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleTryAgain = () => {
    setVehicle(undefined)
    setRegistration('')
    setError('')
    setIsValid(false)
  }

  const nextQuestion = () => {
    dispatch({
      type: 'SAVE',
      name: QuestionKey.VEHICLE_TYPE,
      value: 'CAR'
    })
    dispatch({
      type: 'SAVE',
      name: QuestionKey.DRIVING_LICENCE,
      value: 'Full UK Driving Licence'
    })
    dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.VEHICLE_REGISTRATION,
      value: vehicle
    })

    // Used for Navigation on Prefill Review Page
    if (updateFollowingPrefillStep) {
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, false, false)
      }
    }
  }

  const previousQuestion = () => {
    handlePrevButtonClick(dispatch, {
      name: QuestionKey.VEHICLE_REGISTRATION,
      value: vehicle
    })

    if (updateFollowingPrefillStep) {
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(false, false, false)
      }
    }
  }

  const toggleDontKnow = () => {
    handleTryAgain()
    setDontKnow(!dontKnow)
    setIsValid(true)
  }

  const handleRegChange = (reg: string) => {
    setDontKnow(false)
    setIsValid(VRM_PATTERN.test(reg.trim()))
    setRegistration(reg)
  }

  if (vehicle) {
    return (
      <>
        <GridItem gridColumn={['auto / span 12', '3 / span 8']} mb={2}>
          <AppFormQuestion questionKey={QuestionKey.VEHICLE_REGISTRATION} mt={3} mb={2}>
            Is this your vehicle?
          </AppFormQuestion>
          <VehicleInfoRefinance vehicle={vehicle} />
          <Button
            variant="text"
            onClick={handleTryAgain}
            data-testid="vehicle-registration-try-again"
          >
            Not your vehicle? Enter a different registration
          </Button>
        </GridItem>
        <NavigationButtons
          nextHandler={nextQuestion}
          previousHandler={previousQuestion}
          disablePrevious={disablePrevious ? 'invisible' : undefined}
        />
      </>
    )
  }

  return (
    <GridItem gridColumn={['auto / span 12', '3 / span 8']}>
      <AppFormQuestion questionKey={QuestionKey.VEHICLE_REGISTRATION} mt={3} mb={2}>
        What&apos;s your vehicle registration?
      </AppFormQuestion>
      <MuiTextField
        id="vehicleRegistration"
        name="vehicleRegistration"
        label="Registration"
        inputType="text"
        inputVariant="outlined"
        testId="vehicle-registration"
        value={registration}
        errorMessage={error || undefined}
        onChangeHandler={(event) => handleRegChange(event.currentTarget.value)}
        mb="34px"
      />
      <Box mb={14}>     
        <FormControlLabel
          label="I don't know"
          onChange={toggleDontKnow}
          control={(
            <Checkbox
              checked={dontKnow}
              data-testid="dont-know-checkbox"
            />
          )}
        />
      </Box>
      <NavigationButtons
        nextHandler={dontKnow ? nextQuestion : handleFindVehicle}
        previousHandler={previousQuestion}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
        disableNext={!isValid || loading}
      />
      {loading && <Loading />}
    </GridItem>
  )
}

export default withFade(withQuestion(VehicleRegistrationLookup))
